/* @flow */

import type { NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';

import { LoadableStatus } from '../../../helpers/loadable/loadable';
import { getDistributorId } from '../../../helpers/ui/item/distributor';
import { getItemLiveProgress } from '../../../helpers/ui/item/time';
import { BroadcastStatus } from '../../../helpers/ui/location/Format';
import { BOVodAssetStatus, getTitIdFromProviderInfo } from '../../../helpers/videofutur/metadata';
import { getProgress } from '../../../helpers/viewingHistory/ViewingHistory';
import { ItemContent, ItemLocationKind } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { RecordingOutcome } from '../../../libs/netgemLibrary/v8/types/Npvr';
import { NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR } from '../../../libs/netgemLibrary/v8/types/Realm';
import { TileConfigTileType } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import { type CARD_DATA_MODAL_TYPE, type CardModalStateType, type CompleteCardModalPropType, ImageDisplayType } from './CardModalConstsAndTypes';

const getDisplayType = (imageDisplayType: ImageDisplayType, tileType: TileConfigTileType): string => {
  if (imageDisplayType === ImageDisplayType.Unset) {
    return '';
  }

  if (tileType === TileConfigTileType.Deeplink) {
    return TileConfigTileType.Deeplink as string;
  }

  if (imageDisplayType === ImageDisplayType.Portrait) {
    return TileConfigTileType.Portrait as string;
  }

  return TileConfigTileType.Landscape as string;
};

const getLiveProgress = (cardData?: CARD_DATA_MODAL_TYPE): number | null => {
  if (!cardData) {
    return null;
  }

  const { item } = cardData;
  return getItemLiveProgress(item);
};

const getProgramMetadata = (cardData: CARD_DATA_MODAL_TYPE, state: CardModalStateType): NETGEM_API_V8_METADATA_PROGRAM | null => cardData.programMetadata ?? state.programMetadata;

const getSeriesMetadata = (cardData: CARD_DATA_MODAL_TYPE, state: CardModalStateType): NETGEM_API_V8_METADATA_SERIES | null => cardData.seriesMetadata ?? state.seriesMetadata;

const getWatchingStatus = (props: CompleteCardModalPropType, state: CardModalStateType): number | null => {
  const { cardData, purchaseList, viewingHistory, viewingHistoryStatus } = props;
  const { contentType, isSeries, vodStatus } = state;

  if (!cardData || isSeries) {
    return null;
  }

  const {
    item,
    item: { locType },
  } = cardData;

  if (!vodStatus && locType !== ItemLocationKind.Catchup && locType !== ItemLocationKind.Recording) {
    return null;
  }

  if (vodStatus && contentType === ItemContent.VODOrDeeplink) {
    if (!purchaseList) {
      // Purchase list not retrieved yet
      return null;
    }

    const { status } = vodStatus;
    if (status === BOVodAssetStatus.Locked) {
      // Item has been watched (at least partially) but its rent has expired
      return null;
    }
  }

  if (viewingHistoryStatus !== LoadableStatus.Loaded || locType === ItemLocationKind.ScheduledEvent) {
    return null;
  }

  const programMetadata = getProgramMetadata(cardData, state);
  const seriesMetadata = getSeriesMetadata(cardData, state);
  return getProgress(viewingHistory, item, programMetadata, seriesMetadata, false, vodStatus);
};

const isFavorite = (props: CompleteCardModalPropType, state: CardModalStateType): boolean => {
  const { cardData, favoriteList, wishlist, wishlistStatus } = props;
  const { authority, purchaseInfo, vodLocationsMetadata } = state;

  if (!cardData) {
    return false;
  }

  const programMetadata = getProgramMetadata(cardData, state);

  if (!programMetadata) {
    return false;
  }

  const { id: programId, providerInfo } = programMetadata;
  const distributorId = getDistributorId(vodLocationsMetadata, purchaseInfo, programMetadata);

  if (authority === NETGEM_API_V8_AUTHENT_REALM_VIDEOFUTUR) {
    if (!providerInfo || !distributorId) {
      return false;
    }

    const titId = getTitIdFromProviderInfo(providerInfo, getSeriesMetadata(cardData, state));

    return favoriteList.includes(titId) ?? false;
  }

  if (wishlistStatus !== LoadableStatus.Loaded) {
    return false;
  }

  const seriesMetadata = getSeriesMetadata(cardData, state);
  const assetId = seriesMetadata?.id ?? programId;

  return wishlist.has(assetId);
};

const isItemPlayable = (cardData: CARD_DATA_MODAL_TYPE, broadcastStatus: BroadcastStatus, contentType: ItemContent, isSeries: boolean, recordOutcome: ?RecordingOutcome): boolean => {
  const {
    isOpenFromPlayer,
    item: { locType },
  } = cardData;

  return (
    !isOpenFromPlayer &&
    !isSeries &&
    ((broadcastStatus === BroadcastStatus.Past && locType === ItemLocationKind.Catchup) ||
      broadcastStatus === BroadcastStatus.Live ||
      broadcastStatus === BroadcastStatus.Preview ||
      (locType === ItemLocationKind.Recording && recordOutcome === RecordingOutcome.Recorded) ||
      contentType === ItemContent.NetgemSVOD)
  );
};

const updateTitle = (applicationName: string, title: ?string) => {
  document.title = title ? `${title} | ${applicationName}` : applicationName;
};

export { getDisplayType, getLiveProgress, getWatchingStatus, isFavorite, isItemPlayable, updateTitle };
